import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { userService } from "@/services/userService";
import { UserRequest, UserUpdateRequest } from "@/types/api";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errorHandler";

export const useUsers = () => {
  const queryClient = useQueryClient();

  // Query para listar usuários
  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: () => userService.getUsers(),
  });

  // Mutation para criar usuário
  const createUserMutation = useMutation({
    mutationFn: (userData: UserRequest) => userService.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuário criado com sucesso!");
    },
    onError: (error: any) => {
      const message = extractErrorMessage(error, "Erro ao criar usuário");
      toast.error(message);
    },
  });

  // Mutation para atualizar usuário (usa UserRequest completo)
  const updateUserMutation = useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: UserRequest }) =>
      userService.updateUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuário atualizado com sucesso!");
    },
    onError: (error: any) => {
      const message = extractErrorMessage(error, "Erro ao atualizar usuário");
      toast.error(message);
    },
  });

  // Mutation para deletar usuário
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuário deletado com sucesso!");
    },
    onError: (error: any) => {
      const message = extractErrorMessage(error, "Erro ao deletar usuário");
      toast.error(message);
    },
  });

  return {
    users: usersQuery.data || [],
    isLoading: usersQuery.isLoading,
    error: usersQuery.error,
    createUser: createUserMutation.mutate,
    updateUser: updateUserMutation.mutate,
    deleteUser: deleteUserMutation.mutate,
    isCreating: createUserMutation.isPending,
    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
  };
};

// Hook para buscar um usuário específico
export const useUser = (id: string) => {
  return useQuery({
    queryKey: ["users", id],
    queryFn: () => userService.getUserById(id),
    enabled: !!id,
  });
};

// Hook para buscar usuário por email
export const useUserByEmail = (email: string) => {
  return useQuery({
    queryKey: ["users", "email", email],
    queryFn: () => userService.getUserByEmail(email),
    enabled: !!email,
  });
};

// Hook para o usuário atual
export const useCurrentUser = () => {
  const queryClient = useQueryClient();

  const currentUserQuery = useQuery({
    queryKey: ["users", "me"],
    queryFn: () => userService.getCurrentUser(),
  });

  const updateProfileMutation = useMutation({
    mutationFn: (userData: UserUpdateRequest) =>
      userService.updateCurrentUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "me"] });
      queryClient.invalidateQueries({ queryKey: ["auth", "currentUser"] });
      toast.success("Perfil atualizado com sucesso!");
    },
    onError: (error: any) => {
      const message = extractErrorMessage(error, "Erro ao atualizar perfil");
      toast.error(message);
    },
  });

  return {
    currentUser: currentUserQuery.data,
    isLoading: currentUserQuery.isLoading,
    error: currentUserQuery.error,
    updateProfile: updateProfileMutation.mutate,
    isUpdatingProfile: updateProfileMutation.isPending,
  };
};
